/**
 * Outlook - "what's coming?": everything forward-looking in one
 * place. Full-year projection vs prior years, target tracking, the
 * SoW pipeline bridge (badged, indicative), forward-book quality,
 * stage totals, the next-90-days invoice list and seasonality.
 */
import { useMemo, useState } from "react";
import axios from "axios";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, ResponsiveContainer,
  Legend, Tooltip as RechartsTooltip,
} from "recharts";
import { toast } from "sonner";
import { fmtGbpShort, PanelTitle } from "../staff/CommissionedPanels";
import { GoalsPanel } from "./GoalsPanel";
import { useCfoData, TabState, KpiStrip, Card, SourceBadge, InfoTip } from "./cfoShared";
import { ForwardQualityPanel, ForwardStagePanel, Next90Panel } from "./OverviewExtras";

const API = process.env.REACT_APP_BACKEND_URL;
const YEAR_COLORS = { 2023: "#d6d3d1", 2024: "#3EA8DC", 2025: "#1B4F8A", 2026: "#0D9488", 2027: "#a8a29e" };
const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function OutlookTab() {
  const { data, loading, error } = useCfoData("cfo/outlook");
  const { data: timeline } = useCfoData("revenue-timeline");
  const [targetInput, setTargetInput] = useState("");
  const [saved, setSaved] = useState(null);

  const cumChart = useMemo(() => {
    if (!data) return { rows: [], years: [] };
    const years = Object.keys(data.cumulative)
      .filter((y) => y <= data.current_year)
      .sort();
    const rows = MONTH_LABELS.map((label, idx) => {
      const row = { label };
      for (const y of years) {
        row[y] = data.cumulative[y][idx]?.cum ?? null;
      }
      return row;
    });
    return { rows, years };
  }, [data]);

  if (loading || error) return <TabState loading={loading} error={error} testid="cfo-outlook" />;

  const target = saved ?? data.target;
  const vsLast = data.same_point[data.same_point.length - 1];
  const pace = vsLast ? ((data.billed_ytd / vsLast.ytd_at_same_point - 1) * 100) : null;

  const saveTarget = async () => {
    const value = parseFloat(targetInput.replace(/[^0-9.]/g, ""));
    if (!value) return;
    await axios.post(`${API}/api/proposals/pipeline/cfo/target`, { year: data.current_year, value });
    const projection = data.projection;
    setSaved({
      year: data.current_year, target: value, projection,
      gap: Math.round((value - projection) * 100) / 100,
      on_track: projection >= value,
      required_monthly_new: null,
    });
    toast.success(`Target for ${data.current_year} saved`);
  };

  return (
    <div className="flex flex-col gap-4" data-testid="cfo-outlook">
      <KpiStrip cards={[
        { label: `Billed YTD · ${data.current_year}`, value: fmtGbpShort(data.billed_ytd), tone: "text-teal-700", testid: "outlook-kpi-ytd", info: "out_ytd", sub: pace != null ? `${pace > 0 ? "+" : ""}${pace.toFixed(1)}% vs ${vsLast.year} at this point` : undefined },
        { label: "Contracted remainder", value: fmtGbpShort(data.contracted_remainder), tone: "text-[#1B4F8A]", testid: "outlook-kpi-remainder", info: "out_remainder" },
        { label: `Projected ${data.current_year} landing`, value: fmtGbpShort(data.projection), tone: "text-slate-900", testid: "outlook-kpi-projection", info: "out_projection", sub: vsLast ? `${vsLast.year} finished at ${fmtGbpShort(vsLast.full_year)}` : undefined },
        target
          ? { label: `Target ${target.year}`, value: fmtGbpShort(target.target), tone: target.on_track ? "text-teal-700" : "text-rose-700", testid: "outlook-kpi-target", info: "out_target", sub: target.on_track ? "on track vs contracted" : `${fmtGbpShort(Math.abs(target.gap))} to find${target.required_monthly_new ? ` · ${fmtGbpShort(target.required_monthly_new)}/mo new` : ""}` }
          : { label: "Target", value: "not set", tone: "text-stone-400", testid: "outlook-kpi-target", info: "out_target", sub: "set one below" },
      ]} />

      <GoalsPanel />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card testid="outlook-cumulative" className="lg:col-span-2">
          <div className="flex items-center justify-between mb-1">
            <PanelTitle>Cumulative revenue by year <InfoTip id="out_cumulative" /></PanelTitle>
            <SourceBadge kind="wip" />
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={cumChart.rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={(v) => fmtGbpShort(v)} tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} width={54} />
                <RechartsTooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v, n) => [fmtGbpShort(v), n]} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                {cumChart.years.map((y) => (
                  <Line
                    key={y} dataKey={y} stroke={YEAR_COLORS[y] || "#a8a29e"}
                    strokeWidth={y === data.current_year ? 2.5 : 1.5}
                    dot={false} connectNulls={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card testid="outlook-target-card">
          <PanelTitle>Target · {data.current_year} <InfoTip id="out_target" /></PanelTitle>
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={targetInput}
              onChange={(e) => setTargetInput(e.target.value)}
              placeholder={data.target_value ? `current: £${data.target_value.toLocaleString()}` : "e.g. 5,000,000"}
              data-testid="outlook-target-input"
              className="flex-1 min-w-0 rounded-lg border border-stone-200 px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#1B4F8A]/30"
            />
            <button
              type="button"
              onClick={saveTarget}
              data-testid="outlook-target-save"
              className="px-3 py-1.5 rounded-lg bg-[#1B4F8A] text-white text-xs font-semibold hover:bg-[#163f6f] transition-colors"
            >
              Save
            </button>
          </div>
          {data.pipeline_bridge && (
            <div data-testid="outlook-bridge">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] font-mono uppercase tracking-wider text-stone-500">
                  Pipeline bridge <InfoTip id="out_bridge" className="ml-0.5" />
                </p>
                <SourceBadge kind="pipeline" />
              </div>
              <ul className="space-y-1.5 text-xs">
                <li className="flex justify-between"><span className="text-stone-500">Contracted projection</span><span className="font-mono tabular-nums text-slate-800">{fmtGbpShort(data.pipeline_bridge.contracted_projection)}</span></li>
                <li className="flex justify-between"><span className="text-stone-500">Quoted book ({data.pipeline_bridge.quoted_opportunities} opps)</span><span className="font-mono tabular-nums text-stone-500">{fmtGbpShort(data.pipeline_bridge.quoted_total)}</span></li>
                <li className="flex justify-between"><span className="text-stone-500">Likelihood-weighted pipeline</span><span className="font-mono tabular-nums text-violet-700">+{fmtGbpShort(data.pipeline_bridge.weighted_pipeline)}</span></li>
                <li className="flex justify-between border-t border-stone-100 pt-1.5"><span className="text-slate-800 font-medium">Projection + weighted</span><span className="font-mono tabular-nums text-slate-900 font-semibold">{fmtGbpShort(data.pipeline_bridge.projection_plus_weighted)}</span></li>
                {data.pipeline_bridge.win_rate_needed_pct != null && (
                  <li className="flex justify-between"><span className="text-stone-500">Win rate needed on quoted book</span><span className={`font-mono tabular-nums ${data.pipeline_bridge.win_rate_needed_pct > 30 ? "text-rose-700" : "text-teal-700"}`}>{data.pipeline_bridge.win_rate_needed_pct}%</span></li>
                )}
              </ul>
              <p className="text-[10px] text-stone-400 mt-2">
                Pipeline figures come from the Schedule of Work quoted book and are indicative - never mixed into revenue.
              </p>
            </div>
          )}
        </Card>
      </div>

      {/* Forward book - moved here from Overview so everything
          future-looking lives on one tab */}
      {timeline?.forward_quality && <ForwardQualityPanel fq={timeline.forward_quality} />}

      {timeline && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ForwardStagePanel byStatus={timeline.scheduled_by_status} />
          <Next90Panel upcoming={timeline.upcoming} />
        </div>
      )}

      <Card testid="outlook-seasonality">
        <PanelTitle>Quarterly seasonality <InfoTip id="out_seasonality" /></PanelTitle>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.seasonality} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <XAxis dataKey="year" tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={(v) => fmtGbpShort(v)} tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} width={54} />
              <RechartsTooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v, n) => [fmtGbpShort(v), n]} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Bar dataKey="Q1" fill="#d6d3d1" maxBarSize={22} />
              <Bar dataKey="Q2" fill="#3EA8DC" maxBarSize={22} />
              <Bar dataKey="Q3" fill="#1B4F8A" maxBarSize={22} />
              <Bar dataKey="Q4" fill="#0D9488" maxBarSize={22} radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <p className="text-[10px] text-stone-400 mt-2">
          Q4 has been the biggest quarter every year - December billing concentration. Current-year quarters are partial.
        </p>
      </Card>
    </div>
  );
}
